import React from "react";
import { Box, Loader2, RotateCw, AlertCircle } from "lucide-react";
import { useRenderJob, isActive } from "./useRenderJob";
import { ApprovalPanel } from "./ApprovalPanel";
import { ProgressPanel } from "./ProgressPanel";
import { VideoPanel } from "./VideoPanel";

function Ended({ job, busy, onRetry }) {
  const failed = job.status === "failed";
  return (
    <div className="space-y-3" data-testid={failed ? "render3d-failed" : "render3d-cancelled"}>
      <div className={`flex items-start gap-2 text-sm ${failed ? "text-red-300" : "text-slate-400"}`}>
        <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
        <span data-testid="render3d-error">{failed ? job.error || "The render failed. You weren't charged." : "This video was cancelled. Nothing was charged."}</span>
      </div>
      <button type="button" disabled={busy} onClick={onRetry} data-testid="render3d-retry"
        className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-white/15 text-xs text-slate-200 hover:bg-white/10 transition-colors">
        <RotateCw className="w-3.5 h-3.5" />{failed ? "Retry" : "Start again"}
      </button>
    </div>
  );
}

function AssetInfo({ asset }) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-[11px] text-slate-400 space-y-0.5" data-testid="render3d-asset">
      <div className="text-slate-200"><span className="text-slate-500">Model · </span><span data-testid="render3d-asset-name">{asset.name}</span></div>
      <div className="truncate">
        <span className="text-slate-500">Source · </span>
        <a href={asset.source_url} target="_blank" rel="noreferrer" className="underline decoration-white/20 hover:text-white transition-colors" data-testid="render3d-asset-source">{asset.source}</a>
        <span className="text-slate-500"> · Licence · </span><span data-testid="render3d-asset-licence">{asset.licence}</span>
      </div>
    </div>
  );
}

export function Render3DCard({ jobId }) {
  const { data, busy, approve, cancel, retry } = useRenderJob(jobId);
  if (!data) return <div className="w-full max-w-md h-24 rounded-2xl border border-white/10 bg-white/[0.03] flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-slate-500" /></div>;
  if (data.missing) return null;
  const { job } = data;
  return (
    <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-4 space-y-4 animate-[fadeInUp_.4s_ease-out_both]" data-testid="render3d-card">
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg border border-white/15 flex items-center justify-center shrink-0"><Box className="w-4 h-4 text-white" /></div>
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Blender 3D video</div>
          <div className="text-sm text-white truncate" data-testid="render3d-title">{job.title}</div>
        </div>
      </div>
      {job.asset && <AssetInfo asset={job.asset} />}
      {job.status === "awaiting_approval" && (
        <>
          <ul className="space-y-1 text-xs text-slate-400" data-testid="render3d-storyboard">
            {job.storyboard.map((s) => <li key={s}>{s}</li>)}
          </ul>
          {job.notes.map((n) => <p key={n} className="text-xs text-slate-300 border-l border-white/20 pl-3" data-testid="render3d-note">{n}</p>)}
          <ApprovalPanel job={job} options={data.options} shapes={data.shapes} balance={data.balance} busy={busy} onApprove={approve} onCancel={cancel} />
        </>
      )}
      {isActive(job.status) && <ProgressPanel job={job} busy={busy} onCancel={cancel} />}
      {job.status === "completed" && job.result && <VideoPanel job={job} />}
      {["failed", "cancelled"].includes(job.status) && <Ended job={job} busy={busy} onRetry={retry} />}
    </div>
  );
}
